import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Highlight from '@tiptap/extension-highlight';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import { Color } from '@tiptap/extension-color';
import { TextStyle } from '@tiptap/extension-text-style';
import FontSize from '@tiptap/extension-font-size';
import Image from '@tiptap/extension-image';
import Placeholder from '@tiptap/extension-placeholder';
import classnames from 'classnames';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import {
  FaBold,
  FaItalic,
  FaUnderline,
  FaStrikethrough,
  FaListUl,
  FaListOl,
  FaAlignLeft,
  FaAlignCenter,
  FaAlignRight,
  FaLink,
  FaHighlighter,
  FaRemoveFormat,
  FaFont,
  FaTextHeight,
  FaImage,
  FaUpload,
} from 'react-icons/fa';
import TipTapImageLibraryModal from './TipTapImageLIbraryModal';
import { Accordion } from '../Accordion/Accordion';
import { StylishNewSearchBar } from './StylishNewSearchBar';
import { StylishNewButton } from './StylishNewButton';
import { toast } from 'react-toastify';
import { normalizeMarkdown as normalize } from 'components/Reports/utils/normalizeMarkdown';

// Helper to get computed CSS variable value from _root.scss
const getCssVar = (varName: string): string => {
  return getComputedStyle(document.documentElement)
    .getPropertyValue(varName)
    .trim();
};

// Highlight color palette - uses CSS variables from _root.scss
const getHighlightColors = () => [
  { color: getCssVar('--warning-400'), label: 'Yellow' },
  { color: getCssVar('--error-400'), label: 'Red' },
  { color: getCssVar('--tech-blue-400'), label: 'Blue' },
  { color: getCssVar('--success-400'), label: 'Green' },
  { color: getCssVar('--organizations-orchid'), label: 'Purple' },
  { color: getCssVar('--warning-500'), label: 'Orange' },
];

// Text color palette - uses CSS variables from _root.scss
// Note: White removed as it often exports poorly
const getTextColors = () => [
  { color: getCssVar('--warning-400'), label: 'Yellow' },
  { color: getCssVar('--error-400'), label: 'Red' },
  { color: getCssVar('--tech-blue-400'), label: 'Blue' },
  { color: getCssVar('--success-400'), label: 'Green' },
  { color: getCssVar('--organizations-orchid'), label: 'Purple' },
  { color: getCssVar('--warning-500'), label: 'Orange' },
];

// Font size options - relative sizes for better UX
const fontSizeOptions = [
  { value: '0.875rem', label: 'Small' },
  { value: '1rem', label: 'Normal' },
  { value: '1.25rem', label: 'Large' },
  { value: '1.5rem', label: 'X-Large' },
];

export interface LibraryImage {
  id: string;
  fileName: string;
  fileUrl: string;
  mimetype: string;
  size: string;
  createdAt: string | null;
  updatedAt: string | null;
  signedUrl: string | undefined;
  expiresAt: string | null;
}

interface TiptapRichTextEditorProps {
  value: string;
  onChange?: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  minHeight?: string;
  autoFocus?: boolean;
  readOnly?: boolean;
  showToolbar?: boolean;
  onClickUploadImage?: (files: File[]) => void;
  onClickDeleteImage?: (imageId: string) => void;
  imageLibrary?: LibraryImage[]; // Array of images available in the library for insertion
  imageLibraryName?: string; // Name of the image library (e.g., "Section Library") to display in the UI
  normalizeMarkdown?: boolean; // Whether to normalize markdown content (e.g., from SITREP sections) for better display in the editor
}

export const processImageContentForSave = (
  content: string,
  imageLibrary?: LibraryImage[]
) => {
  if (!imageLibrary) return content;

  const parser = new DOMParser();
  const doc = parser.parseFromString(content, 'text/html');
  const images = doc.querySelectorAll('img');

  images.forEach((img) => {
    const signedUrl = img.getAttribute('src');

    const fileUrl = imageLibrary.find((image) =>
      signedUrl?.includes(image.fileUrl)
    )?.fileUrl;

    if (fileUrl) {
      img.setAttribute('src', fileUrl);
    }
  });

  return doc.body.innerHTML;
};

export const TiptapRichTextEditor: React.FC<TiptapRichTextEditorProps> = ({
  value,
  onChange,
  placeholder = 'Start typing...',
  disabled = false,
  className,
  minHeight = '150px',
  autoFocus = false,
  readOnly = false,
  showToolbar = true,
  onClickUploadImage,
  onClickDeleteImage,
  imageLibrary,
  imageLibraryName,
  normalizeMarkdown = false,
}) => {
  const [showHighlightMenu, setShowHighlightMenu] = useState(false);
  const [showColorMenu, setShowColorMenu] = useState(false);
  const [showFontSizeMenu, setShowFontSizeMenu] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  // Compute colors from CSS variables once on mount
  const highlightColors = useMemo(() => getHighlightColors(), []);
  const textColors = useMemo(() => getTextColors(), []);

  const [imageLibrarySearchTerm, setImageLibrarySearchTerm] = useState('');

  const filteredImages = useMemo(() => {
    if (!imageLibrary) return [];
    return imageLibrary.filter((image) =>
      image.fileName
        .toLowerCase()
        .includes(imageLibrarySearchTerm.toLowerCase())
    );
  }, [imageLibrary, imageLibrarySearchTerm]);

  const processContentForDisplay = useMemo(() => {
    return (content: string, imageLibrary?: LibraryImage[]) => {
      const cleanContent = normalizeMarkdown ? normalize(content) : content;

      if (!imageLibrary) return cleanContent;

      const parser = new DOMParser();
      const doc = parser.parseFromString(cleanContent, 'text/html');
      const images = doc.querySelectorAll('img');

      if (imageLibrary) {
        const imageMap = new Map(
          imageLibrary.map((image) => [image.fileUrl, image.signedUrl])
        );

        images.forEach((img) => {
          const fileUrl = img.getAttribute('src');

          const signedUrl = fileUrl ? imageMap.get(fileUrl) : undefined;
          if (signedUrl) {
            img.setAttribute('src', signedUrl);
            img.setAttribute('width', '400'); // Set a default width for display purposes
          }
        });
      }

      return doc.body.innerHTML;
    };
  }, [normalizeMarkdown]);

  const handleContentChange = (content: string) => {
    if (onChange) {
      onChange(content);
    }
  };

  const handleDeleteImage = async (image: LibraryImage) => {
    onClickDeleteImage?.(image.id);
  };

  const handleImageUploadClick = () => {
    fileInputRef.current?.click();
  };

  const uploadFilesChangeHandler = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const files = event.target.files;
    if (!files || files.length <= 0) return;
    if (files.length > 10) {
      toast.info('Maximum upload limit is 10 files at a time');
      return;
    }

    if (onClickUploadImage) {
      onClickUploadImage(Array.from(files));
    }

    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const supportedFormatsTooltip = (
    <Tooltip id="supported-formats-tooltip">
      Supported formats: jpg, png, gif, bmp, webp, svg
    </Tooltip>
  );

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
        // Disabled here because they are configured separately below
        link: false,
        underline: false,
      }),
      Underline,
      Highlight.configure({
        multicolor: true,
      }),
      TextStyle,
      Color,
      FontSize,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Link.configure({
        openOnClick: !readOnly,
        HTMLAttributes: {
          target: '_blank',
          rel: 'noopener noreferrer',
        },
      }),
      Image.configure({
        allowBase64: true,
        HTMLAttributes: {
          class: 'tiptap-editor__inserted-image',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content: value,
    editable: !disabled && !readOnly,
    autofocus: autoFocus ? 'end' : false,
    onUpdate: ({ editor }) => {
      if (handleContentChange) {
        const html = editor.getHTML();
        handleContentChange(html);
      }
    },
  });

  // Update editor content when value prop changes externally
  useEffect(() => {
    const cleanedContent = processContentForDisplay(value, imageLibrary);
    if (editor && cleanedContent !== editor.getHTML()) {
      editor.commands.setContent(cleanedContent);
    }
  }, [value, editor, imageLibrary, processContentForDisplay]);

  // Update editable state
  useEffect(() => {
    if (editor) {
      editor.setEditable(!disabled && !readOnly);
    }
  }, [disabled, readOnly, editor]);

  if (!editor) {
    return null;
  }

  const handleSetLink = () => {
    if (linkUrl) {
      editor.chain().focus().setLink({ href: linkUrl }).run();
    }
    setShowLinkDialog(false);
    setLinkUrl('');
  };

  const handleRemoveLink = () => {
    editor.chain().focus().unsetLink().run();
    setShowLinkDialog(false);
  };

  const openLinkDialog = () => {
    const previousUrl = editor.getAttributes('link').href;
    setLinkUrl(previousUrl || '');
    setShowLinkDialog(true);
  };

  const onClickAddImage = () => {
    setShowImageDialog(true);
  };

  const renderTooltip = (text: string) => (
    <Tooltip id={`tooltip-${text.toLowerCase().replace(/\s/g, '-')}`}>
      {text}
    </Tooltip>
  );

  return (
    <>
      <div
        className={classnames('tiptap-editor', className, {
          'tiptap-editor--disabled': disabled,
          'tiptap-editor--readonly': readOnly,
        })}
        style={readOnly ? undefined : { minHeight }}
      >
        {/* Toolbar - only show in edit mode */}
        {showToolbar && !readOnly && (
          <div className="tiptap-editor__toolbar">
            {/* Text Formatting Group */}
            <div className="tiptap-editor__toolbar-group">
              <OverlayTrigger placement="top" overlay={renderTooltip('Bold')}>
                <button
                  type="button"
                  onClick={() => editor.chain().focus().toggleBold().run()}
                  disabled={!editor.can().chain().focus().toggleBold().run()}
                  className={classnames('tiptap-editor__button', {
                    'tiptap-editor__button--active': editor.isActive('bold'),
                  })}
                  aria-label="Bold"
                >
                  <FaBold />
                </button>
              </OverlayTrigger>
              <OverlayTrigger placement="top" overlay={renderTooltip('Italic')}>
                <button
                  type="button"
                  onClick={() => editor.chain().focus().toggleItalic().run()}
                  disabled={!editor.can().chain().focus().toggleItalic().run()}
                  className={classnames('tiptap-editor__button', {
                    'tiptap-editor__button--active': editor.isActive('italic'),
                  })}
                  aria-label="Italic"
                >
                  <FaItalic />
                </button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip('Underline')}
              >
                <button
                  type="button"
                  onClick={() => editor.chain().focus().toggleUnderline().run()}
                  disabled={
                    !editor.can().chain().focus().toggleUnderline().run()
                  }
                  className={classnames('tiptap-editor__button', {
                    'tiptap-editor__button--active':
                      editor.isActive('underline'),
                  })}
                  aria-label="Underline"
                >
                  <FaUnderline />
                </button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip('Strikethrough')}
              >
                <button
                  type="button"
                  onClick={() => editor.chain().focus().toggleStrike().run()}
                  disabled={!editor.can().chain().focus().toggleStrike().run()}
                  className={classnames('tiptap-editor__button', {
                    'tiptap-editor__button--active': editor.isActive('strike'),
                  })}
                  aria-label="Strikethrough"
                >
                  <FaStrikethrough />
                </button>
              </OverlayTrigger>
            </div>

            {/* Highlighting Group */}
            <div className="tiptap-editor__toolbar-group">
              <div className="tiptap-editor__highlight-wrapper">
                <OverlayTrigger
                  placement="top"
                  overlay={renderTooltip('Highlight')}
                >
                  <button
                    type="button"
                    onClick={() => setShowHighlightMenu(!showHighlightMenu)}
                    className={classnames('tiptap-editor__button', {
                      'tiptap-editor__button--active':
                        editor.isActive('highlight'),
                    })}
                    aria-label="Highlight"
                  >
                    <FaHighlighter />
                  </button>
                </OverlayTrigger>
                {showHighlightMenu && (
                  <div className="tiptap-editor__color-dropdown">
                    {highlightColors.map(({ color, label }) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => {
                          editor.chain().focus().setHighlight({ color }).run();
                          setShowHighlightMenu(false);
                        }}
                        className={classnames('tiptap-editor__color-swatch', {
                          'tiptap-editor__color-swatch--active':
                            editor.isActive('highlight', { color }),
                        })}
                        style={{ backgroundColor: color }}
                        title={label}
                        aria-label={`Highlight ${label}`}
                      />
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        editor.chain().focus().unsetHighlight().run();
                        setShowHighlightMenu(false);
                      }}
                      className="tiptap-editor__button tiptap-editor__button--clear"
                      title="Remove Highlight"
                      aria-label="Remove Highlight"
                    >
                      <FaRemoveFormat />
                    </button>
                  </div>
                )}
              </div>

              {/* Text Color */}
              <div className="tiptap-editor__highlight-wrapper">
                <OverlayTrigger
                  placement="top"
                  overlay={renderTooltip('Text Color')}
                >
                  <button
                    type="button"
                    onClick={() => setShowColorMenu(!showColorMenu)}
                    className={classnames('tiptap-editor__button', {
                      'tiptap-editor__button--active':
                        editor.isActive('textStyle'),
                    })}
                    aria-label="Text Color"
                  >
                    <FaFont />
                  </button>
                </OverlayTrigger>
                {showColorMenu && (
                  <div className="tiptap-editor__color-dropdown">
                    {textColors.map(({ color, label }) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => {
                          editor.chain().focus().setColor(color).run();
                          setShowColorMenu(false);
                        }}
                        className={classnames('tiptap-editor__color-swatch', {
                          'tiptap-editor__color-swatch--active':
                            editor.isActive('textStyle', { color }),
                        })}
                        style={{ backgroundColor: color }}
                        title={label}
                        aria-label={`Text Color ${label}`}
                      />
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        editor.chain().focus().unsetColor().run();
                        setShowColorMenu(false);
                      }}
                      className="tiptap-editor__button tiptap-editor__button--clear"
                      title="Remove Color"
                      aria-label="Remove Color"
                    >
                      <FaRemoveFormat />
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Font Size Group */}
            <div className="tiptap-editor__toolbar-group">
              <div className="tiptap-editor__highlight-wrapper">
                <OverlayTrigger
                  placement="top"
                  overlay={renderTooltip('Font Size')}
                >
                  <button
                    type="button"
                    onClick={() => setShowFontSizeMenu(!showFontSizeMenu)}
                    className="tiptap-editor__button"
                    aria-label="Font Size"
                  >
                    <FaTextHeight />
                  </button>
                </OverlayTrigger>
                {showFontSizeMenu && (
                  <div className="tiptap-editor__font-size-dropdown">
                    {fontSizeOptions.map(({ value, label }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => {
                          editor.chain().focus().setFontSize(value).run();
                          setShowFontSizeMenu(false);
                        }}
                        className={classnames(
                          'tiptap-editor__font-size-option',
                          {
                            'tiptap-editor__font-size-option--active':
                              editor.getAttributes('textStyle').fontSize ===
                              value,
                          }
                        )}
                      >
                        {label}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        editor.chain().focus().unsetFontSize().run();
                        setShowFontSizeMenu(false);
                      }}
                      className="tiptap-editor__font-size-option tiptap-editor__font-size-option--reset"
                    >
                      Reset
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Lists & Alignment Group */}
            <div className="tiptap-editor__toolbar-group">
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip('Bullet List')}
              >
                <button
                  type="button"
                  onClick={() =>
                    editor.chain().focus().toggleBulletList().run()
                  }
                  className={classnames('tiptap-editor__button', {
                    'tiptap-editor__button--active':
                      editor.isActive('bulletList'),
                  })}
                  aria-label="Bullet List"
                >
                  <FaListUl />
                </button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip('Numbered List')}
              >
                <button
                  type="button"
                  onClick={() =>
                    editor.chain().focus().toggleOrderedList().run()
                  }
                  className={classnames('tiptap-editor__button', {
                    'tiptap-editor__button--active':
                      editor.isActive('orderedList'),
                  })}
                  aria-label="Numbered List"
                >
                  <FaListOl />
                </button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip('Align Left')}
              >
                <button
                  type="button"
                  onClick={() =>
                    editor.chain().focus().setTextAlign('left').run()
                  }
                  className={classnames('tiptap-editor__button', {
                    'tiptap-editor__button--active': editor.isActive({
                      textAlign: 'left',
                    }),
                  })}
                  aria-label="Align Left"
                >
                  <FaAlignLeft />
                </button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip('Align Center')}
              >
                <button
                  type="button"
                  onClick={() =>
                    editor.chain().focus().setTextAlign('center').run()
                  }
                  className={classnames('tiptap-editor__button', {
                    'tiptap-editor__button--active': editor.isActive({
                      textAlign: 'center',
                    }),
                  })}
                  aria-label="Align Center"
                >
                  <FaAlignCenter />
                </button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip('Align Right')}
              >
                <button
                  type="button"
                  onClick={() =>
                    editor.chain().focus().setTextAlign('right').run()
                  }
                  className={classnames('tiptap-editor__button', {
                    'tiptap-editor__button--active': editor.isActive({
                      textAlign: 'right',
                    }),
                  })}
                  aria-label="Align Right"
                >
                  <FaAlignRight />
                </button>
              </OverlayTrigger>
            </div>

            {/* Links & Actions Group */}
            <div className="tiptap-editor__toolbar-group">
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip('Insert Link')}
              >
                <button
                  type="button"
                  onClick={openLinkDialog}
                  className={classnames('tiptap-editor__button', {
                    'tiptap-editor__button--active': editor.isActive('link'),
                  })}
                  aria-label="Insert Link"
                >
                  <FaLink />
                </button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="top"
                overlay={renderTooltip('Clear Formatting')}
              >
                <button
                  type="button"
                  onClick={() =>
                    editor
                      .chain()
                      .focus()
                      .unsetColor()
                      .unsetHighlight()
                      .clearNodes()
                      .unsetAllMarks()
                      .run()
                  }
                  className="tiptap-editor__button"
                  aria-label="Clear Formatting"
                >
                  <FaRemoveFormat />
                </button>
              </OverlayTrigger>
              {imageLibrary && imageLibrary?.length > 0 && (
                <OverlayTrigger
                  placement="top"
                  overlay={renderTooltip('Add Image from Section Library')}
                >
                  <button
                    type="button"
                    onClick={onClickAddImage}
                    className="tiptap-editor__button"
                    aria-label="Clear Formatting"
                  >
                    <FaImage />
                  </button>
                </OverlayTrigger>
              )}
              {onClickUploadImage && (
                <OverlayTrigger
                  placement="top"
                  overlay={renderTooltip('Upload Image to Section Library')}
                >
                  <button
                    type="button"
                    onClick={handleImageUploadClick}
                    className="tiptap-editor__button"
                    aria-label="Clear Formatting"
                  >
                    <FaUpload />
                  </button>
                </OverlayTrigger>
              )}
            </div>
          </div>
        )}

        {/* Editor Content */}
        <div
          className="tiptap-editor__content"
          style={readOnly ? undefined : { minHeight }}
        >
          <EditorContent editor={editor} />
        </div>

        {/* Link Dialog */}
        {showLinkDialog && (
          <div className="tiptap-editor__link-dialog">
            <div className="tiptap-editor__link-dialog-content">
              <input
                type="url"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                placeholder="Enter URL"
                className="form-control"
                autoFocus
              />
              <div className="tiptap-editor__link-dialog-actions">
                <button
                  type="button"
                  onClick={handleSetLink}
                  className="btn btn-primary btn-sm"
                >
                  Set Link
                </button>
                {editor.isActive('link') && (
                  <button
                    type="button"
                    onClick={handleRemoveLink}
                    className="btn btn-danger btn-sm"
                  >
                    Remove Link
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setShowLinkDialog(false)}
                  className="btn btn-secondary btn-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}
        {showImageDialog && imageLibrary && (
          <TipTapImageLibraryModal
            show={!!showImageDialog}
            imageLibrary={imageLibrary}
            editor={editor}
            onClose={() => setShowImageDialog(false)}
          />
        )}
      </div>
      {imageLibrary && onClickUploadImage && !readOnly && (
        <div className="m-3">
          <Accordion
            key="images-accordion"
            eventKey="images-accordion"
            header={
              <span className="fw-semibold text-muted">{imageLibraryName}</span>
            }
            showIcon
            classes={{ card: 'border border-neutral-500' }}
          >
            {imageLibrary?.length === 0 && (
              <div className="p-3">
                <em className="text-muted">
                  No images attached to this SITREP section.
                </em>
              </div>
            )}
            {(imageLibrary ?? []).length > 0 && (
              <>
                <div className="m-2">
                  <StylishNewSearchBar
                    value={imageLibrarySearchTerm}
                    onChangeSearchTerm={setImageLibrarySearchTerm}
                    placeholder="Search images..."
                  />
                </div>
                <div className="d-flex m-3 flex-wrap gap-2">
                  {filteredImages?.map((image) => (
                    <div key={image.fileName} className="position-relative">
                      <div className="position-absolute d-flex justify-content-between align-items-center bg-dark bg-opacity-75 start-0 top-0 w-100 px-2 py-1 text-white">
                        <span
                          className="text-truncate"
                          style={{ maxWidth: '70%' }}
                        >
                          {image.fileName}
                        </span>
                        <div className="d-flex gap-2">
                          <OverlayTrigger
                            key={image.fileName}
                            placement="top"
                            overlay={renderTooltip('Click to insert')}
                          >
                            <StylishNewButton
                              className="button--secondary button--sml"
                              onClick={(_e) => {
                                editor
                                  .chain()
                                  .focus()
                                  .setImage({ src: image.fileUrl })
                                  .run();
                              }}
                            >
                              Add
                            </StylishNewButton>
                          </OverlayTrigger>
                          <StylishNewButton
                            className="button--secondary button--sml"
                            onClick={(_e) => handleDeleteImage(image)}
                          >
                            Delete
                          </StylishNewButton>
                        </div>
                      </div>
                      <img
                        src={image.signedUrl}
                        alt={image.fileName}
                        className="img-fluid"
                        width={400}
                      />
                    </div>
                  ))}
                </div>
              </>
            )}
            <div className="m-3">
              <StylishNewButton
                className="button--primary"
                onClick={handleImageUploadClick}
                type="button"
              >
                Upload Image
              </StylishNewButton>
              <OverlayTrigger placement="top" overlay={supportedFormatsTooltip}>
                <i
                  className="fa fa-info-circle ms-2"
                  style={{ cursor: 'pointer', fontSize: '16px' }}
                  aria-label="Supported file formats"
                />
              </OverlayTrigger>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              name="file"
              multiple
              accept={'jpg, png, gif, bmp, webp, svg'}
              style={{ display: 'none' }}
              onChange={uploadFilesChangeHandler}
            />
          </Accordion>
        </div>
      )}
    </>
  );
};

TiptapRichTextEditor.displayName = 'TiptapRichTextEditor';

export default TiptapRichTextEditor;
